import React, { useState, useEffect, useRef } from 'react'
import { DateTime } from 'luxon'
import {
  AdjustmentsHorizontalIcon,
  ArrowDownTrayIcon,
  EllipsisVerticalIcon
} from '@heroicons/react/24/outline'
import TableComponent from 'components/common/TableComponent'
import MonitorCard from 'components/Dashboard/MonitorCard'
import StatCards from 'components/Dashboard/StatCards'
import { makeRequest } from 'services/request.service'
import { useAuth } from 'auth/AuthContext'
import { parseDate } from 'utils'

// GET request for daily agro summary data
const getDailyAgroSummaryData = async (farmId, filterDate) => {
  try {
    const response = await makeRequest({
      method: 'GET',
      url: `/dashboard/${farmId}/agro-summary?filterDate=${filterDate}`,
      headers: {
        Authorization: `Bearer ${localStorage.getItem('secret')}`
      }
    })
    return response.data
  } catch (error) {
    console.error('Error fetching daily agro summary data:', error)
    throw error
  }
}

const AgroActionsTable = ({ agroActionsSummary }) => {
  const tableData = {
    hasCheckbox: false,
    columns: [
      { key: 'blockName', label: 'Block Name' },
      { key: 'crop', label: 'Crop' },
      { key: 'variety', label: 'Variety' },
      { key: 'activityName', label: 'Activity Name' },
      { key: 'units', label: 'Units' },
      { key: 'percentageOfCompletion', label: 'Percentage of completion', cell: (value) => `${value}%` }
    ],
    rows: agroActionsSummary
  }
  return <TableComponent tableData={tableData} defaultSortColumn={tableData.columns[0]?.key} />
}

const WorkersMonitorTable = ({ workersMonitor }) => {
  const tableData = {
    hasCheckbox: false,
    columns: [
      { key: 'workerId', label: 'Worker ID' },
      { key: 'workerName', label: 'Worker Name' },
      { key: 'activity', label: 'Activity' },
      { key: 'blockName', label: 'Block Name' },
      { key: 'totalPlants', label: 'Units' },
      { key: 'totalHours', label: 'Total work hours' },
      { key: 'plantsPerHour', label: 'Average rate per hour' },
      { key: 'groupName', label: 'Group Name' },
      { key: 'firstScan', label: 'First Scan', cell: (value) => parseDate(value) },
      { key: 'lastScan', label: 'Last Scan', cell: (value) => parseDate(value) }
    ],
    rows: workersMonitor
  }
  return <TableComponent tableData={tableData} defaultSortColumn={tableData.columns[0]?.key} />
}

const DailyAgroSummary = () => {
  const { user } = useAuth()
  const hasFetched = useRef(false)
  const [agroSummaryData, setAgroSummaryData] = useState({
    dailyAgroSummary: {
      totalActions: 0,
      workerCount: 0,
      averagePerWorker: 0
    },
    agroActionsSummary: [],
    workersMonitor: [],
    loading: true,
    error: null
  })

  // Get current date in Mexico City timezone
  const currentDate = DateTime.now().setZone('America/Mexico_City').toFormat('yyyy-MM-dd')
  const formattedDate = DateTime.now().setZone('America/Mexico_City').toFormat('dd.MM.yyyy')

  // New state for selected date
  const [selectedDate, setSelectedDate] = useState(currentDate)

  const fetchAgroSummaryData = async (dateToFetch = selectedDate) => {
    if (!user?.farmId) return

    try {
      setAgroSummaryData(prev => ({ ...prev, loading: true, error: null }))

      const data = await getDailyAgroSummaryData(user.farmId, dateToFetch)

      setAgroSummaryData(prev => ({
        ...prev,
        dailyAgroSummary: data.dailyAgroSummary || prev.dailyAgroSummary,
        agroActionsSummary: data.agroActionsSummary || [],
        workersMonitor: data.workersMonitor || [],
        loading: false
      }))
    } catch (error) {
      console.error('Error fetching agro summary:', error)
      setAgroSummaryData(prev => ({
        ...prev,
        loading: false,
        error: 'Failed to load agro summary data'
      }))
    }
  }

  useEffect(() => {
    if (hasFetched.current) return
    hasFetched.current = true

    fetchAgroSummaryData(selectedDate)
  }, [user?.farmId])

  // Refetch when selectedDate changes
  useEffect(() => {
    if (!hasFetched.current) return
    fetchAgroSummaryData(selectedDate)
  }, [selectedDate])

  const cardData = [
    {
      title: 'Total Actions',
      value: agroSummaryData.dailyAgroSummary.totalActions,
      unit: 'Units',
      color: 'bg-red-700'
    },
    {
      title: 'Cost Per Unit',
      value: '$0.01',
      unit: 'Per Unit',
      color: 'bg-red-500'
    },
    {
      title: 'Worker Count',
      value: agroSummaryData.dailyAgroSummary.workerCount,
      unit: 'Workers',
      color: 'bg-red-600'
    },
    {
      title: 'Avg Per Worker',
      value: agroSummaryData.dailyAgroSummary.averagePerWorker,
      unit: 'Units',
      color: 'bg-red-400'
    }
  ]

  return (
    <div className='p-6 bg-gray-50 min-h-screen'>
      <div className='flex justify-between items-start mb-4'>
        <div>
          <h1 className='text-2xl font-semibold text-red-600'>Daily Agro Summary</h1>
          <p className='text-sm text-gray-600'>
            Last Agro Day ({formattedDate}){' '}
          </p>
        </div>
        {/* Date Picker */}
        <div className='flex items-center gap-2'>
          <label htmlFor='agro-date' className='text-sm text-gray-700'>Select Date:</label>
          <input
            id='agro-date'
            type='date'
            className='border rounded px-2 py-1 text-sm'
            value={selectedDate}
            max={currentDate}
            onChange={e => setSelectedDate(e.target.value)}
          />
        </div>
      </div>

      <hr className='border-t border-gray-300 mb-6' />

      {agroSummaryData.loading ? (
        <div className='text-gray-500'>Loading agro summary data...</div>
      ) : agroSummaryData.error ? (
        <div className='text-red-500'>{agroSummaryData.error}</div>
      ) : (
        <>
          <StatCards cards={cardData} />

          <MonitorCard title='Agro Actions Summary' date={formattedDate} hasData={agroSummaryData.agroActionsSummary.length > 0} color='red'>
            <AgroActionsTable agroActionsSummary={agroSummaryData.agroActionsSummary} />
          </MonitorCard>

          <MonitorCard title='Workers Monitor' date={formattedDate} hasData={agroSummaryData.workersMonitor.length > 0} color='red'>
            <WorkersMonitorTable workersMonitor={agroSummaryData.workersMonitor} />
          </MonitorCard>
        </>
      )}
    </div>
  )
}

export default DailyAgroSummary
